import { Component, Optional, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';
import { FacilityLocation } from '../../../models/facility';
import { OrganizationProviderInsert, PracticeFilter, ProviderService } from '../../../services/provider.service';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { TableModule } from 'primeng/table';
import { PanelModule } from 'primeng/panel';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DividerModule } from 'primeng/divider';
import { AccordionModule } from 'primeng/accordion';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { DialogModule } from 'primeng/dialog';
import { Practice } from '../../../models/practice';
import { OrganizationProvider, ProviderLocation } from '../../../models/provider';
import { ILocation } from '../../../models/location';
import { AddressEditorComponent } from '../../locations/address-editor/address-editor.component';
import { FacilityLocationSearchComponent } from '../../medical/facilities/facility-search/facility-location-search.component';
import { PracticeRatesService } from '../../../services/practice-rates.service';
import { PracticeMarkupFormComponent } from '../practice-markup-form/practice-markup-form.component';
import { PracticeDiscountFormComponent } from '../practice-discount-form/practice-discount-form.component';

@Component({
  selector: 'app-provider-manager',
  standalone: true,
  imports: [
    FormsModule, NgIf, NgFor,
    InputTextModule, ButtonModule, ButtonGroupModule, TableModule,
    PanelModule, TagModule, DividerModule, AccordionModule,
    IconFieldModule, InputIconModule, OverlayPanelModule, DialogModule,
    AddressEditorComponent, FacilityLocationSearchComponent,
    PracticeMarkupFormComponent, PracticeDiscountFormComponent
],
  templateUrl: './provider-manager.component.html',
  styleUrl: './provider-manager.component.scss'
})
export class ProviderManagerComponent
{
  @ViewChild('aliasOverlay') aliasOverlay!: OverlayPanel;

  get showPracticeRates(): boolean { return this._practiceRatesService != null; }

  markupDialogVisible = false;
  discountDialogVisible = false;
  ratesEin = '';
  ratesPracticeName = '';

  filter: PracticeFilter = {};
  results: Practice[] = [];
  loading: boolean = false;
  searched: boolean = false;
  expandedRows: { [ein: string]: boolean } = {};

  selectedNames: { [ein: string]: string } = {};
  pendingNames: { [ein: string]: string } = {};
  savingNames = new Set<string>();
  activePractice: Practice | null = null;

  readonly facilityPreviewLimit = 5;
  expandedFacilities = new Set<string>();

  showOrgDialog = false;
  orgDialogPractice: Practice | null = null;
  orgNpiInput = '';
  addingOrg = false;

  showProDialog = false;
  proDialogPractice: Practice | null = null;
  proDialogProvider: OrganizationProvider | null = null;
  proDialogLocation: ProviderLocation | null = null;
  proNpiInput = '';
  addingPro = false;

  deletingOrg = new Set<string>();
  deletingPro = new Set<string>();

  orgNameDrafts: { [npi: string]: string } = {};
  savingOrgName = new Set<string>();

  locationsLoading = new Set<string>();
  locationsLoaded = new Set<string>();

  locationEditorVisible = false;
  editingAddress?: ILocation;
  private locationEditContext: { practice: Practice, provider: OrganizationProvider, index: number } | null = null;

  linkLocationDialogVisible = false;
  linkingFacility?: FacilityLocation;
  private linkLocationContext: { practice: Practice, provider: OrganizationProvider } | null = null;

  deletingLocation = new Set<number>();

  proEditDialogVisible = false;
  proEditFirstName = '';
  proEditLastName = '';
  savingProEdit = false;
  private proEditContext: { location: ProviderLocation, index: number } | null = null;

  constructor(
    private _providerService: ProviderService,
    @Optional() private _practiceRatesService: PracticeRatesService | null
  )
  {

  }

  editProviderLocation(practice: Practice, provider: OrganizationProvider, index: number): void
  {
    this.locationEditContext = { practice, provider, index };
    this.editingAddress = this.toILocation(provider.providerLocations[index]);
    this.locationEditorVisible = true;
  }

  addProviderLocation(practice: Practice, provider: OrganizationProvider): void
  {
    this.locationEditContext = { practice, provider, index: -1 };
    this.editingAddress = undefined;
    this.locationEditorVisible = true;
  }

  async saveProviderLocation(location: ILocation): Promise<void>
  {
    const context = this.locationEditContext;
    if (!context) return;
    const { practice, provider, index } = context;

    if (index === -1)
    {
      const added = await this._providerService.addProviderLocation(practice.ein, provider.npi, this.toProviderLocation(location));
      provider.providerLocations.push(added);
    }
    else
    {
      const existing = provider.providerLocations[index];
      const updated = await this._providerService.updateProviderLocation(practice.ein, provider.npi, existing.locationId, this.toProviderLocation(location, existing.professionals));
      provider.providerLocations[index] = updated;
    }
  }

  openLinkLocationDialog(practice: Practice, provider: OrganizationProvider): void
  {
    this.linkLocationContext = { practice, provider };
    this.linkingFacility = undefined;
    this.linkLocationDialogVisible = true;
  }

  async confirmLinkLocation(): Promise<void>
  {
    const context = this.linkLocationContext;
    if (!context || !this.linkingFacility) return;
    const { practice, provider } = context;

    const linked = await this._providerService.linkProviderLocation(practice.ein, provider.npi, this.linkingFacility.location.id);
    if (!provider.providerLocations.some(l => l.locationId === linked.locationId))
      provider.providerLocations.push(linked);

    this.linkLocationDialogVisible = false;
    this.linkingFacility = undefined;
  }

  async removeProviderLocation(practice: Practice, provider: OrganizationProvider, index: number): Promise<void>
  {
    const location = provider.providerLocations[index];
    this.deletingLocation.add(location.locationId);
    await this._providerService.removeProviderLocation(practice.ein, provider.npi, location.locationId)
      .finally(() => this.deletingLocation.delete(location.locationId));
    provider.providerLocations.splice(index, 1);
  }

  private toILocation(location: ProviderLocation): ILocation
  {
    return {
      id: location.locationId,
      street1: location.street1,
      street2: location.street2,
      city: location.city,
      state: location.state,
      zipCode: location.zipCode,
      latitude: 0,
      longitude: 0,
    };
  }

  private toProviderLocation(location: ILocation, professionals: ProviderLocation['professionals'] = []): ProviderLocation
  {
    return new ProviderLocation(
      location.id,
      location.street1,
      location.city,
      location.state,
      location.zipCode,
      professionals,
      location.street2,
    );
  }

  visibleFacilities(practice: Practice): OrganizationProvider[]
  {
    if (!practice.facilities) return [];
    return this.expandedFacilities.has(practice.ein)
      ? practice.facilities
      : practice.facilities.slice(0, this.facilityPreviewLimit);
  }

  toggleFacilitiesExpansion(ein: string): void
  {
    if (this.expandedFacilities.has(ein)) this.expandedFacilities.delete(ein);
    else this.expandedFacilities.add(ein);
  }

  private orgProviderKey(ein: string, npi: string): string
  {
    return `${ein}:${npi}`;
  }

  async onOrgProviderToggle(practice: Practice, provider: OrganizationProvider, opened: boolean): Promise<void>
  {
    if (!opened) return;
    const key = this.orgProviderKey(practice.ein, provider.npi);
    if (this.locationsLoaded.has(key)) return;
    this.locationsLoading.add(key);
    const locations = await this._providerService.getOrganzationalProviderLocations(practice.ein, provider.npi)
      .finally(() => this.locationsLoading.delete(key));
    provider.providerLocations = locations;
    this.locationsLoaded.add(key);
  }

  isLoadingLocations(practice: Practice, provider: OrganizationProvider): boolean
  {
    return this.locationsLoading.has(this.orgProviderKey(practice.ein, provider.npi));
  }

  get hasFilter(): boolean
  {
    return (this.filter.name?.trim().length ?? 0) >= 2
        || (this.filter.ein?.trim().length ?? 0) >= 2
        || (this.filter.location?.trim().length ?? 0) >= 2;
  }

  displayName(practice: Practice): string
  {
    return this.pendingNames[practice.ein] ?? this.selectedNames[practice.ein] ?? practice.name;
  }

  showAliases(event: Event, practice: Practice): void
  {
    if (!practice.aliases?.length) return;
    this.activePractice = practice;
    this.aliasOverlay.toggle(event);
  }

  selectAlias(alias: string): void
  {
    if (this.activePractice)
      this.pendingNames[this.activePractice.ein] = alias;
    this.aliasOverlay.hide();
  }

  cancelPending(ein: string): void
  {
    delete this.pendingNames[ein];
  }

  startEditPracticeName(practice: Practice): void
  {
    if (this.pendingNames[practice.ein] === undefined)
      this.pendingNames[practice.ein] = practice.name;
  }

  async savePracticeName(practice: Practice): Promise<void>
  {
    const name = this.pendingNames[practice.ein];
    if (!name) return;
    this.savingNames.add(practice.ein);
    const updated = await this._providerService.updatePractice(practice.ein, { name })
      .finally(() => this.savingNames.delete(practice.ein));
    practice.name = updated.name;
    this.selectedNames[practice.ein] = updated.name;
    delete this.pendingNames[practice.ein];
  }


  openOrgDialog(practice: Practice): void
  {
    this.orgDialogPractice = practice;
    this.orgNpiInput = '';
    this.showOrgDialog = true;
  }

  closeOrgDialog(): void
  {
    this.showOrgDialog = false;
    this.orgDialogPractice = null;
    this.orgNpiInput = '';
  }

  async confirmAddOrgProvider(): Promise<void>
  {
    const npi = this.orgNpiInput.trim();
    if (!npi || !this.orgDialogPractice) return;
    this.addingOrg = true;
    const insert: OrganizationProviderInsert = { npi };
    const added = await this._providerService.addOrganizationProviderToPractice(this.orgDialogPractice.ein, insert)
      .finally(() => this.addingOrg = false);
    if (!this.orgDialogPractice.facilities)
      this.orgDialogPractice.facilities = [];
    if (!this.orgDialogPractice.facilities.some(f => f.npi === added.npi))
      this.orgDialogPractice.facilities.push(added);
    this.closeOrgDialog();
  }

  async deleteOrgProvider(practice: Practice, providerNpi: string): Promise<void>
  {
    this.deletingOrg.add(providerNpi);
    await this._providerService.removeOrganizationProviderFromPractice(practice.ein, providerNpi)
      .finally(() => this.deletingOrg.delete(providerNpi));
    practice.facilities = practice.facilities.filter(f => f.npi !== providerNpi);
  }

  startEditOrgName(provider: OrganizationProvider): void
  {
    this.orgNameDrafts[provider.npi] = provider.organizationName;
  }

  cancelEditOrgName(npi: string): void
  {
    delete this.orgNameDrafts[npi];
  }

  async saveOrgName(provider: OrganizationProvider): Promise<void>
  {
    const name = this.orgNameDrafts[provider.npi];
    if (!name) return;
    this.savingOrgName.add(provider.npi);
    const updated = await this._providerService.updateOrganizationProvider(provider.npi, { organizationName: name })
      .finally(() => this.savingOrgName.delete(provider.npi));
    provider.organizationName = updated.organizationName;
    delete this.orgNameDrafts[provider.npi];
  }

  openMarkupDialog(practice: Practice): void
  {
    this.ratesEin = practice.ein;
    this.ratesPracticeName = practice.name;
    this.markupDialogVisible = true;
  }

  openDiscountDialog(practice: Practice): void
  {
    this.ratesEin = practice.ein;
    this.ratesPracticeName = practice.name;
    this.discountDialogVisible = true;
  }

  openProDialog(practice: Practice, provider: OrganizationProvider, location: ProviderLocation): void
  {
    this.proDialogPractice = practice;
    this.proDialogProvider = provider;
    this.proDialogLocation = location;
    this.proNpiInput = '';
    this.showProDialog = true;
  }

  closeProDialog(): void
  {
    this.showProDialog = false;
    this.proDialogPractice = null;
    this.proDialogProvider = null;
    this.proDialogLocation = null;
    this.proNpiInput = '';
  }

  async confirmAddProfessional(): Promise<void>
  {
    const npi = this.proNpiInput.trim();
    if (!npi || !this.proDialogPractice || !this.proDialogProvider || !this.proDialogLocation) return;
    this.addingPro = true;
    const location = this.proDialogLocation;
    const added = await this._providerService.addProfessionalProvider(
        this.proDialogPractice.ein, this.proDialogProvider.npi, location.locationId, npi)
      .finally(() => this.addingPro = false);
    if (!location.professionals.some(p => p.npi === added.npi))
      location.professionals.push(added);
    this.closeProDialog();
  }

  async deleteProfessional(practice: Practice, provider: OrganizationProvider, location: ProviderLocation, professionalNpi: string): Promise<void>
  {
    this.deletingPro.add(professionalNpi);
    await this._providerService.removeProfessionalProvider(practice.ein, provider.npi, location.locationId, professionalNpi)
      .finally(() => this.deletingPro.delete(professionalNpi));
    location.professionals = location.professionals.filter(p => p.npi !== professionalNpi);
  }

  openEditProfessionalDialog(location: ProviderLocation, index: number): void
  {
    const pro = location.professionals[index];
    this.proEditContext = { location, index };
    this.proEditFirstName = pro.firstName;
    this.proEditLastName = pro.lastName;
    this.proEditDialogVisible = true;
  }

  closeProEditDialog(): void
  {
    this.proEditDialogVisible = false;
    this.proEditContext = null;
  }

  async confirmEditProfessional(): Promise<void>
  {
    const context = this.proEditContext;
    if (!context) return;
    const npi = context.location.professionals[context.index].npi;
    this.savingProEdit = true;
    const updated = await this._providerService.updateProfessionalProvider(npi, {
        firstName: this.proEditFirstName.trim(),
        lastName: this.proEditLastName.trim(),
      })
      .finally(() => this.savingProEdit = false);
    context.location.professionals[context.index] = updated;
    this.closeProEditDialog();
  }

  async search()
  {
    if (!this.hasFilter) return;
    this.loading = true;
    this.searched = true;
    const results = await this._providerService.searchPractices(this.filter)
      .finally(() => this.loading = false);
    this.results = results.filter(p => p.facilities?.length > 0);
  }

  clear()
  {
    this.filter = {};
    this.results = [];
    this.searched = false;
    this.expandedRows = {};
    this.expandedFacilities.clear();
    this.selectedNames = {};
    this.pendingNames = {};
    this.savingNames.clear();
    this.activePractice = null;
    this.locationsLoading.clear();
    this.locationsLoaded.clear();
  }

  onKeyDown(event: KeyboardEvent)
  {
    if (event.key === 'Enter') this.search();
  }

  formatAddress(facility: FacilityLocation): string
  {
    const l = facility.location;
    if (!l) return '—';
    const parts = [l.street1, l.street2, l.city, l.state, l.zipCode].filter(Boolean);
    return parts.join(', ');
  }
}
